import axios, { AxiosError, AxiosResponse } from 'axios';
import config from '@/../service/project.config.js';
import { CwxBackend } from '@/types/cwxBackend';
import { ApiError } from '@/types/commonTypes';

export default {
  async getLoggedUserSettings(): Promise<CwxBackend.IUserInfo> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getLoggedUserInfo`;

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },

  async setUserFrontendSettingsAsync(
    view: CwxBackend.FrontendViews,
    enableNewFrontend: boolean,
  ): Promise<CwxBackend.IWorkflowResult | ApiError> {
    const updateCommand = {
      isNewFrontedEnabled: enableNewFrontend,
      view,
    } as CwxBackend.IUpdateUserFrontendViewSettingCommand;
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/settings/updateUserFrontendSettings`;

    return axios.post(endpointUrl, updateCommand).then((response) => {
      return response.data;
    });
  },

  async findUsersForPeoplePicker(
    payload: CwxBackend.IUserFindRequest,
  ): Promise<CwxBackend.IUserPickerResultDto> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/findUsersForPeoplePicker`;

    return axios.post(endpointUrl, payload).then((response) => {
      return response.data;
    });
  },

  async getUsersForPeoplePicker(payload: string[]): Promise<CwxBackend.IUserPickerDto[]> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getUsersForPeoplePickerByIds`;

    return axios.post(endpointUrl, payload).then((response) => {
      return response.data;
    });
  },

  async getLineManagersForUsers(payload: string[]): Promise<CwxBackend.IUserViewModel[]> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getLineManagersForUsers`;

    return axios.post(endpointUrl, payload).then((response) => {
      return response.data;
    });
  },

  async getUserDetails(userId?: string): Promise<CwxBackend.IProfileDetailsDto> {
    let endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/GetUserDetails`;
    if (userId) {
      endpointUrl = `${endpointUrl}?userId=${userId}`;
    }

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
async getUserEditModel(userId?: string): Promise<CwxBackend.IProfileEditDto> {
    let endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/GetUserEditModel`;
    if (userId) {
      endpointUrl = `${endpointUrl}?userId=${userId}`;
    }

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
  async updateUserDetails(
    updateUserDetailsCommand: CwxBackend.IUpdateProfileDetailsCommand,
  ): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/UpdateUserDetails`;
    return axios.post(endpointUrl, updateUserDetailsCommand).then((response) => {
      return response;
    });
  },
  async getUserReplacementSettings(): Promise<CwxBackend.ICurrentUserReplacementDto> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getUserReplacementSettings`;

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
  async createSelfReplacement(
    command: CwxBackend.ICreateReplacementCommand,
  ): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/createSelfReplacement`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
  async closeReplacement(command: CwxBackend.ICloseReplacementCommand): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/closeReplacement`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
  async getChangePassword(userId?: string): Promise<CwxBackend.IChangePasswordDto> {
    let endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getChangePasswordModel`;
    if (userId) {
      endpointUrl = `${endpointUrl}?userId=${userId}`;
    }

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
  async changePassword(
    command: CwxBackend.IChangePasswordCommand,
  ): Promise<AxiosResponse<ApiError>> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/changePassword`;

    try {
      const response = await axios.post(endpointUrl, command);
      return response;
    } catch (error: unknown) {
      const typedError = error as AxiosError;
      if (typedError.response && typedError.response.status === 302) {
        window.location.href = typedError.response.headers.location;
      }
      throw error;
    }
  },
  async getAccountSettings(userId?: string): Promise<CwxBackend.IAccountSettingsDto> {
    let endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getAccountSettingsModel`;
    if (userId) {
      endpointUrl = `${endpointUrl}?userId=${userId}`;
    }

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
  async updateAccountSetting(
    command: CwxBackend.IUpdateAccountSettingsCommand,
  ): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/updateAccountSetting`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
  async resetOnboarding(command: CwxBackend.IResetOnboardingCommand): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/resetOnboarding`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
  async deleteAccount(command: CwxBackend.IDeleteUserCommand): Promise<AxiosResponse<ApiError>> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/deleteAccount`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
  async getNotificationSettings(
    userId?: string,
  ): Promise<CwxBackend.IAccountNotificationSettingsDto> {
    let endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/getNotificationsSettings`;
    if (userId) {
      endpointUrl = `${endpointUrl}?userId=${userId}`;
    }

    return axios.get(endpointUrl).then((response) => {
      return response.data;
    });
  },
  async updateAccountNotificationSetting(
    command: CwxBackend.IUpdateAccountNotificationSettingsCommand,
  ): Promise<AxiosResponse> {
    const endpointUrl = `${config.backend}/${config.apiPrefixes.v1}/users/updateNotificationsSettings`;

    return axios.post(endpointUrl, command).then((response) => {
      return response;
    });
  },
};
